import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { IconButton, useMediaQuery, useTheme } from '@material-ui/core';
import {
  Chat,
  ChevronLeft,
  ChevronRight,
  Download,
  List
} from '@carbon/icons-react';
import AppConfig from '@config';
import useViewerCommunication, {
  OpenAnnotationCommentResultEnum
} from '@components/Chat/ChatMenu/Viewer/useViewerCommunication';
import useFiles from '@hooks/useFiles';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { clsx, colorSet, useSnackbar } from '@components/Provider';
import PDFViewer from '@components/PDFViewer';
import { Image } from '@components/Image';
import MobileViewerDimensionsButton from './components/MobileViewerDimensionsButton';
import MobileViewerDimensionsDrawer from './components/MobileViewerDimensionsDrawer';
import ViewerMenuTabs from './ViewerMenuTabs';
import ViewerRightDrawer from './ViewerRightDrawer';
import ViewerFileList, { FileAvatar } from './ViewerFileList';
import DrawingFileViewer from './DrawingFileViewer';
import AnnotationList from './components/AnnotationList';
import { TabValue } from './ViewerContainer';
import useStyles from './useStyles';
import { UfoImage, TutorialImage, TutorialImageM } from './assets';
import { FileCategory, ViewerMode } from './types';
import {
  CapaQuotationChatRoomAnnotationComment,
  CapaQuotationChatRoomSharedFiles,
  useFetchQuotationChatRoomAnnotationCommentsQuery
} from '@generated/graphql';
import {
  ViewerApiContextProvider,
  ViewerDataContext,
  ViwerDataContextProvider
} from './context/ViewerContext';
import DeletedAnnotationDialog from './DeletedAnnotationDialog';
import { IFile } from '@routes/Common/type';
import { useLocation } from 'react-router-dom';

interface Props {
  mode: ViewerMode;
  tab: TabValue;
  matches: boolean;
  rightDrawer: boolean;
  files: CapaQuotationChatRoomSharedFiles[];
  currentItem?: CapaQuotationChatRoomSharedFiles;
  fileCategory: FileCategory;
  isFirstItem: boolean;
  isLastItem: boolean;
  canDownload: boolean;
  onHandleRightDrawer: (value: boolean) => void;
  onChangeTab: (e: any, value: TabValue) => void;
  onChangeIndex: (_index: number, type?: 'sub' | 'add') => void;
  onDownload: (file?: CapaQuotationChatRoomSharedFiles) => void;
  refetchFiles?: () => void;
}

const CHAT_BUBBLE_ANNOTATION_PARAMS = [
  'selectedFileId',
  'annotationId',
  'commentId'
];

const ViewerPresenter = ({
  mode,
  tab,
  matches,
  rightDrawer,
  files,
  currentItem,
  fileCategory,
  isFirstItem,
  isLastItem,
  canDownload,
  onHandleRightDrawer,
  onChangeTab,
  onChangeIndex,
  onDownload,
  refetchFiles
}: Props) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const chatRoomId = searchParams.get('chatRoomId') ?? '';
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('xs'));
  const viewerRef = useRef<HTMLIFrameElement | null>(null);
  const clickedAnnotation = useRef<{
    fileId: string;
    annotationId: string;
  } | null>(null);
  const {
    data,
    refetch: refetchAnnotations
  } = useFetchQuotationChatRoomAnnotationCommentsQuery({
    variables: {
      input: {
        chatRoomId: chatRoomId as string
      }
    },
    fetchPolicy: 'network-only',
    skip: !chatRoomId
  });
  const annotations: CapaQuotationChatRoomAnnotationComment[] = useMemo(
    () => data?.fetchQuotationChatRoomAnnotationComments.data ?? [],
    [data?.fetchQuotationChatRoomAnnotationComments.data]
  );
  const viewerCommunicationTools = useViewerCommunication({
    viewerRef,
    fileId: currentItem?.quotationSharedFileId as string,
    filterType: currentItem?.quotationSharedFileType ?? undefined,
    files,
    fileListTab: tab,
    refetchFiles,
    refetchAnnotations
  });
  const {
    dimensions,
    draftAnnotationData,
    fileAnnotations,
    selectedAnnotationId,
    ...viewerApi
  }: ViewerDataContext = viewerCommunicationTools;
  const viewerData = {
    dimensions,
    draftAnnotationData,
    fileAnnotations,
    selectedAnnotationId
  };
  const [listTab, setListTab] = useState<'file' | 'annotation'>('file');
  const [
    deletedAnnotationDialogState,
    setDeletedAnnotationDialogState
  ] = useState<{ open: boolean; type: 'annotation' | 'comment' }>({
    open: false,
    type: 'annotation'
  });
  const classes = useStyles();
  const { onDownloadZipFile } = useFiles();
  const { enqueueSnackbar } = useSnackbar();
  const isChat = !!chatRoomId;

  const onClickAnnotation = useCallback(
    async (e: MouseEvent, fileId: string, annotationId: string) => {
      setListTab('file');
      onChangeTab(e, 'ALL');
      clickedAnnotation.current = {
        fileId,
        annotationId
      };
    },
    //eslint-disable-next-line
    [
      onChangeIndex,
      onChangeTab,
      viewerCommunicationTools.onOpenAnnotation,
      files
    ]
  );

  const onDownloadAll = async () => {
    if (!canDownload) return enqueueSnackbar('파일 다운로드 권한이 없습니다.');

    try {
      await onDownloadZipFile(files as IFile[]);
      return enqueueSnackbar('파일 다운로드에 성공했습니다.');
    } catch (e) {
      return enqueueSnackbar(
        '파일 다운로드에 실패했습니다. 다시 시도해주세요.'
      );
    }
  };

  const ListRouter = useMemo(() => {
    switch (listTab) {
      case 'file': {
        return (
          <ViewerFileList
            tab={tab}
            mode={mode}
            files={files}
            currentItem={currentItem}
            onChangeIndex={onChangeIndex}
          />
        );
      }
      case 'annotation': {
        return (
          <AnnotationList
            onClickAnnotation={onClickAnnotation}
            annotations={annotations}
          />
        );
      }
    }
  }, [
    annotations,
    listTab,
    currentItem,
    files,
    mode,
    onChangeIndex,
    onClickAnnotation,
    tab
  ]);

  useEffect(() => {
    const hasAnnotationParams = CHAT_BUBBLE_ANNOTATION_PARAMS.every((param) =>
      window.location.search.includes(param)
    );

    if (!hasAnnotationParams) return;

    const selectedFileId = searchParams.get('selectedFileId') || '';
    const annotationId = searchParams.get('annotationId') || '';

    onClickAnnotation(undefined as any, selectedFileId, annotationId);
    setListTab('annotation');

    //eslint-disable-next-line
  }, [onClickAnnotation]);

  useEffect(() => {
    if (!files.length) return;
    const { annotationId, fileId } = clickedAnnotation.current || {
      annotationId: null,
      fileId: null
    };
    const fileIndex = files.findIndex((file) => file.fileId === fileId);
    if (fileIndex === -1) return;
    onChangeIndex(fileIndex);

    if (!annotationId || !fileId) return;

    const sharedFileId = files?.[fileIndex]?.quotationSharedFileId ?? '';
    viewerCommunicationTools
      .onOpenAnnotation(annotationId, sharedFileId)
      .then((result) => {
        if (result !== OpenAnnotationCommentResultEnum.SUCCESS) {
          setDeletedAnnotationDialogState({
            open: true,
            type:
              result === OpenAnnotationCommentResultEnum.ANNOTATION_DELETE
                ? 'annotation'
                : 'comment'
          });
        } else {
          // 주석리스트에서 클릭한 주석 초기화
          clickedAnnotation.current = null;

          // 채팅 버블을 통해 넘어온 queryString 초기화, 초기화 안해주면 useEffect 무한호출.
          const url = new URL(window.location.href);
          CHAT_BUBBLE_ANNOTATION_PARAMS.forEach((param) => {
            url.searchParams.delete(param);
          });
          window.history.pushState(null, '', url.toString());
        }
      });

    //eslint-disable-next-line
  }, [files, listTab]);

  return (
    <ViwerDataContextProvider value={viewerData}>
      <ViewerApiContextProvider value={viewerApi}>
        <DeletedAnnotationDialog
          onClose={() => {
            setDeletedAnnotationDialogState((prev) => ({
              ...prev,
              open: false
            }));
          }}
          dialogState={deletedAnnotationDialogState}
        />
        <div
          className={clsx(classes['Chat-Menu-Viewer-root'], {
            'full-height': mode === 'default'
          })}
        >
          <div className={clsx(classes['Viewer-Header-root'])}>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="500"
            >
              CAPA 뷰어
            </CustomizedTypography>

            <div className="mobile-header-icon-group">
              {!matches && (
                <IconButton
                  disabled={!canDownload}
                  onClick={() => onDownload(currentItem)}
                >
                  <Download size={24} />
                </IconButton>
              )}
              {matches && isChat && (
                <IconButton
                  onClick={() => {
                    setListTab('annotation');
                    onHandleRightDrawer(true);
                  }}
                >
                  <Chat size={24} color={colorSet.gray900} />
                </IconButton>
              )}
              {matches && (
                <IconButton
                  onClick={() => {
                    setListTab('file');
                    onHandleRightDrawer(true);
                  }}
                >
                  <List size={24} color={colorSet.gray900} />
                </IconButton>
              )}
            </div>
          </div>

          <div className={clsx(classes['Viewer-Header-root'], 'empty')} />

          <div className={classes['Viewer-Content-Wrapper']}>
            <div
              className={clsx(classes['Viewer-File-List-root'], {
                default: mode === 'default'
              })}
            >
              <div className="file-list-header">
                {!matches && (
                  <div className="list-header-tab">
                    <CustomizedButton
                      id={'viewer-filelist-button'}
                      variant="text"
                      onClick={() => setListTab('file')}
                      className={listTab === 'file' ? 'selected-list' : ''}
                    >
                      <CustomizedTypography
                        variant="subtitle1"
                        fontWeight="700"
                        color="inherit"
                      >
                        파일 리스트
                      </CustomizedTypography>
                    </CustomizedButton>
                    {isChat && (
                      <CustomizedButton
                        id={'viewer-chat-button'}
                        variant="text"
                        onClick={() => {
                          if (!isChat) return;
                          setListTab('annotation');
                        }}
                        className={
                          listTab === 'annotation' ? 'selected-list' : ''
                        }
                      >
                        <CustomizedTypography
                          variant="subtitle1"
                          fontWeight="700"
                          color="inherit"
                        >
                          주석
                        </CustomizedTypography>
                      </CustomizedButton>
                    )}
                  </div>
                )}

                {matches && (
                  <>
                    <FileAvatar
                      fileId={currentItem?.fileId as string}
                      fileName={currentItem?.fileName as string}
                      isActive={true}
                    />
                    <IconButton
                      disabled={!canDownload}
                      onClick={() => onDownload(currentItem)}
                    >
                      <Download size={24} />
                    </IconButton>
                  </>
                )}
              </div>
              <div className="file-list-header empty" />
              {matchesXs &&
                fileCategory === 'DRAWING_FILE' &&
                viewerCommunicationTools.isDimensionsEnable && (
                  <>
                    <MobileViewerDimensionsButton />
                    <MobileViewerDimensionsDrawer />
                  </>
                )}
              <ViewerRightDrawer
                open={rightDrawer}
                onClose={() => onHandleRightDrawer(false)}
                matches={matches}
                listTabValue={listTab}
                tab={
                  (!isChat || listTab === 'file') && (
                    <ViewerMenuTabs
                      mode={mode}
                      tab={tab}
                      onChangeTab={onChangeTab}
                    />
                  )
                }
                onDownloadAll={onDownloadAll}
              >
                {ListRouter}
              </ViewerRightDrawer>
            </div>
            <div
              className={clsx(classes['Viewer-Content'], {
                'image-viewer': fileCategory === 'IMAGE',
                'error-viewer': fileCategory === 'ERROR',
                'tutorial-image': fileCategory === 'TUTORIAL',
                'pdf-viewer': fileCategory === 'PDF',
                'default': mode === 'default'
              })}
            >
              <IconButton
                disableTouchRipple
                disableRipple
                className="icon-button left"
                onClick={() => onChangeIndex(-1, 'sub')}
                disabled={isFirstItem}
              >
                <ChevronLeft />
              </IconButton>

              <IconButton
                disableTouchRipple
                disableRipple
                className={`icon-button right ${
                  mode === 'chat' ? '' : 'default'
                }`}
                onClick={() => onChangeIndex(+1, 'add')}
                disabled={isLastItem}
              >
                <ChevronRight />
              </IconButton>

              {!currentItem && (
                <>
                  <Image src={UfoImage} />
                  <CustomizedTypography
                    variant="body2"
                    fontWeight="500"
                    color="textSecondary"
                  >
                    {tab === 'CHAT' ? '채팅' : '요청서'}에서 첨부한 파일이
                    없습니다.
                  </CustomizedTypography>
                </>
              )}

              {fileCategory === 'IMAGE' && (
                <Image
                  src={`${AppConfig.fileApiUrl}/file/${currentItem?.fileId}`}
                  onError={(e) => e.currentTarget.src === UfoImage}
                />
              )}

              {fileCategory === 'DRAWING_FILE' && (
                <DrawingFileViewer
                  {...currentItem}
                  viewerRef={viewerRef}
                  key={currentItem?.fileId}
                />
              )}

              {currentItem && fileCategory === 'ERROR' && (
                <>
                  <Image src={UfoImage} />
                  <CustomizedTypography
                    align="center"
                    fontWeight="500"
                    color="textPrimary"
                    variant="body1"
                    gutterBottoms={6}
                  >
                    {currentItem?.fileName} 파일은
                  </CustomizedTypography>
                  <CustomizedTypography
                    align="center"
                    fontWeight="500"
                    color="textPrimary"
                    variant="body1"
                    gutterBottoms={32}
                  >
                    현재 CAPA 뷰어로 볼 수 없습니다.
                  </CustomizedTypography>
                  <CustomizedButton
                    onClick={() => onDownload(currentItem)}
                    variant="outlined"
                    color="primary"
                    className="download"
                    fullWidth
                  >
                    다운로드
                  </CustomizedButton>
                </>
              )}

              {fileCategory === 'PDF' && (
                <PDFViewer
                  fileUrl={`${AppConfig.fileApiUrl}/file/${currentItem?.fileId}`}
                  fileId={currentItem?.fileId ?? ''}
                  fileName={currentItem?.fileName ?? ''}
                />
              )}

              {fileCategory === 'TUTORIAL' && (
                <Image
                  width={matches ? 'auto' : '100%'}
                  height={matches ? '100%' : 'auto'}
                  src={matches ? TutorialImageM : TutorialImage}
                  style={{
                    minHeight: 672,
                    objectFit: 'cover'
                  }}
                  onError={(e) => e.currentTarget.src === UfoImage}
                />
              )}
            </div>
          </div>
        </div>
      </ViewerApiContextProvider>
    </ViwerDataContextProvider>
  );
};

export default ViewerPresenter;
